import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { num, money, formatApiError } from "@/lib/api";
import { Card, PageHeader, Btn, Modal, Input, Select, EmptyState, KPICard, Th, Td } from "@/components/admin/ui";

function TeamDrawer({ teamId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (teamId) api.get(`/teams/${teamId}`).then((r) => setData(r.data)).catch(() => {});
    else setData(null);
  }, [teamId]);
  if (!teamId) return null;
  const m = data?.metrics || {};
  return (
    <Modal open={!!teamId} onClose={onClose} title={data ? `${data.team.name} · Team Performance` : "Loading…"} wide>
      {data ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KPICard label="Team Size" value={num(data.team_size)} />
            <KPICard label="Active" value={num(data.active_workers)} accent="#10B981" />
            <KPICard label="Completion" value={`${m.completion_rate || 0}%`} accent="#06B6D4" />
            <KPICard label="Revenue" value={money(m.revenue)} accent="#1D4ED8" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KPICard label="Tasks" value={num(m.tasks_assigned)} />
            <KPICard label="Leads" value={num(m.leads)} accent="#06B6D4" />
            <KPICard label="Meetings" value={num(m.meetings)} accent="#8B5CF6" />
            <KPICard label="Sales" value={num(m.sales)} accent="#10B981" />
          </div>
          <div>
            <h4 className="font-head font-semibold text-white mb-2">Members (Team → Worker)</h4>
            <div className="max-h-64 overflow-y-auto cc-scroll space-y-2">
              {data.workers.map((w) => (
                <div key={w.id} className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-[#0B0F19] border border-white/10" data-testid={`team-member-${w.worker_id}`}>
                  <div><span className="text-slate-200 text-sm font-medium">{w.name}</span> <span className="font-mono-metric text-[#1D4ED8] text-xs">{w.worker_id}</span></div>
                  <span className="text-xs text-slate-500">{w.status}</span>
                </div>
              ))}
              {data.workers.length === 0 && <p className="text-slate-500 text-sm">No workers in this team yet.</p>}
            </div>
          </div>
        </div>
      ) : <p className="text-slate-400">Loading team data…</p>}
    </Modal>
  );
}

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [cities, setCities] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", city_id: "" });

  const load = useCallback(() => { api.get("/teams").then((r) => setTeams(r.data.items)).catch(() => {}); }, []);
  useEffect(() => { load(); api.get("/cities").then((r) => setCities(r.data.items)); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/teams", form); toast.success(`Team created · ${data.name}`); setOpen(false); setForm({ name: "", city_id: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const cityName = (id) => cities.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-6">
      <PageHeader title="Teams" subtitle={`${num(teams.length)} teams · click a team to drill down`} action={<Btn data-testid="create-team-btn" onClick={() => setOpen(true)}>+ Create Team</Btn>} />
      <Card>
        {teams.length === 0 ? <EmptyState title="No teams yet" desc="Create your first team to group workers and track team performance." action={<Btn onClick={() => setOpen(true)}>+ Create Team</Btn>} /> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-white/10"><tr><Th>Team</Th><Th>City</Th><Th>Workers</Th><Th>Status</Th></tr></thead>
              <tbody>
                {teams.map((t) => (
                  <tr key={t.id} onClick={() => setSelected(t.id)} className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer" data-testid={`team-row-${t.name}`}>
                    <Td className="font-medium text-white">{t.name}</Td>
                    <Td className="text-slate-400">{cityName(t.city_id)}</Td>
                    <Td className="font-mono-metric text-[#1D4ED8]">{num(t.worker_count)}</Td>
                    <Td className="text-slate-400">{t.status}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Modal open={open} onClose={() => setOpen(false)} title="Create Team">
        <form onSubmit={create} className="space-y-3">
          <Input label="Team name" data-testid="team-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Team Alpha" />
          <Select label="City" data-testid="team-city-select" required value={form.city_id} onChange={(e) => setForm({ ...form, city_id: e.target.value })}>
            <option value="">Select city</option>{cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Btn data-testid="team-submit" className="w-full">Create Team</Btn>
        </form>
      </Modal>
      <TeamDrawer teamId={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
